import React from 'react';
import { getLikerDisplayName } from '../../utils/displayName';
import { getTopReactions } from '../../utils/reactions';
import ReactionIcon from '../icon/ReactionIcon';
import ReactionButton from './ReactionButton';
import ReplyButton from '../icon/replybutton';
import RepostButton from '../icon/repostbutton';
import ShareButton from '../icon/shareicome';

/**
 * Facebook/LinkedIn-style reaction / reply / share bar.
 */
export default function PostActionsSection({
  postKey,
  postId,
  likeCount = 0,
  userLiked = false,
  userReaction = null,
  whoLiked = {},
  replyCount = 0,
  showReplies = false,
  onToggleReplies,
  onLike,
  onReact,
  onToggleReply,
  shareUrl,
  onShowLikesModal,
  repostCount = 0,
  userReposted = false,
  onRepost,
}) {
  const likedUsers = whoLiked[postKey] || [];
  const activeReaction = userReaction || (userLiked ? 'like' : null);
  const topReactions = getTopReactions(likedUsers, 3);

  const formatReactionNames = () => {
    if (likedUsers.length === 0) return null;
    if (likedUsers.length === 1) return getLikerDisplayName(likedUsers[0]);
    if (likedUsers.length === 2) {
      return `${getLikerDisplayName(likedUsers[0])} and ${getLikerDisplayName(likedUsers[1])}`;
    }
    const names = likedUsers.slice(0, 2).map((u) => getLikerDisplayName(u)).join(', ');
    return `${names} and ${likedUsers.length - 2} others`;
  };

  const reactionText = formatReactionNames();

  const handleReact = (type) => {
    if (onReact) {
      onReact(type);
      return;
    }
    // Fallback for callers that only support simple like/unlike
    if (type === null) {
      if (userLiked || activeReaction) onLike?.(postId);
    } else if (!userLiked && !activeReaction) {
      onLike?.(postId);
    }
  };

  const handleShare = () => {
    if (!shareUrl) return;
    if (navigator.share) {
      navigator.share({ title: 'ReallyLesson Post', url: shareUrl }).catch(() => {
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
      });
    } else {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const actionBtnStyle = {
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0px',
    padding: '6px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    flex: 1,
    justifyContent: 'center',
    color: '#65676b',
    fontWeight: '600',
    fontSize: '15px',
    transition: 'background-color 0.2s ease',
  };

  return (
    <div style={{ marginTop: '12px' }}>
      {(likeCount > 0 || repostCount > 0) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            marginBottom: '8px',
            padding: '0 4px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: onShowLikesModal ? 'pointer' : 'default',
              flex: 1,
              minWidth: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (likeCount > 0) onShowLikesModal?.(postKey);
            }}
            onMouseEnter={(e) => {
              if (onShowLikesModal && likeCount > 0) e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            {likeCount > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {topReactions.map((type, idx) => (
                    <span
                      key={type}
                      style={{
                        marginLeft: idx === 0 ? 0 : -4,
                        zIndex: topReactions.length - idx,
                        background: '#fff',
                        borderRadius: '50%',
                        width: 18,
                        height: 18,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 0 1px #fff',
                      }}
                    >
                      <ReactionIcon type={type} size={12} />
                    </span>
                  ))}
                </div>
                {reactionText && (
                  <span style={{ fontSize: '14px', color: '#65676b' }}>{reactionText}</span>
                )}
              </>
            )}
          </div>
          {repostCount > 0 && (
            <span style={{ fontSize: '14px', color: '#65676b', whiteSpace: 'nowrap' }}>
              {repostCount} repost{repostCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      <div style={{ height: '1px', backgroundColor: '#e4e6eb', margin: '8px 0' }} />

      <div
        className="post-action-bar"
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          padding: '4px 0',
        }}
      >
        <ReactionButton
          userReaction={activeReaction}
          onReact={handleReact}
          iconSize={16}
        />

        <button
          type="button"
          onClick={() => onToggleReply?.(postId)}
          aria-label="Reply"
          title="Reply"
          style={actionBtnStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f2f2f2';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <ReplyButton />
        </button>

        {onRepost && (
          <button
            type="button"
            onClick={() => onRepost(postId)}
            style={{
              ...actionBtnStyle,
              color: userReposted ? '#0a66c2' : '#65676b',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f2f2f2';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label={userReposted ? 'Undo repost' : 'Repost'}
            title={userReposted ? 'Undo repost' : 'Repost'}
          >
            <RepostButton count={repostCount} active={userReposted} />
          </button>
        )}

        <button
          type="button"
          onClick={handleShare}
          style={actionBtnStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f2f2f2';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          aria-label="Share"
          title="Share"
        >
          <ShareButton />
        </button>
      </div>

      {replyCount > 0 && onToggleReplies && (
        <div style={{ padding: '4px 12px', marginTop: '4px' }}>
          <button
            type="button"
            onClick={() => onToggleReplies(postId)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#65676b',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              padding: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            {showReplies
              ? 'Hide replies'
              : `View ${replyCount} repl${replyCount === 1 ? 'y' : 'ies'}`}
          </button>
        </div>
      )}
    </div>
  );
}
